import React, { useEffect } from 'react';
import { useSocket } from '../../context/SocketContext';
import { useAuthStore } from '../../stores/useAuthStore';
import { useChatStore } from '../../stores/useChatStore';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const NotificationListener = () => {
  const { socket } = useSocket();
  const currentUser = useAuthStore((state) => state.currentUser);
  const setCurrentUser = useAuthStore((state) => state.setCurrentUser);
  const navigate = useNavigate();

  useEffect(() => {
    if (!socket || !currentUser) return;

    // 🟢 Listener: XP Ganado
    const handleXpGained = (data) => {
      const { points, reason, totalPoints, newLevel } = data;
      
      // 🟢 FIX: Solo actualizar estado si realmente hay cambios
      if (totalPoints !== undefined) {
          const updatedUser = { ...currentUser, creatorPoints: totalPoints, creatorLevel: newLevel || currentUser.creatorLevel };
          setCurrentUser(updatedUser);
      }

      toast.success(
        <div className="flex flex-col">
          <span className="font-bold flex items-center">
            <i className="fas fa-star text-yellow-400 mr-2"></i>
            +{points} XP
          </span>
          <span className="text-xs text-gray-600">{reason || '¡Buen trabajo!'}</span>
        </div>,
        { 
            duration: 4000,
            position: 'bottom-right',
            style: {
                border: '1px solid #EAB308',
                padding: '16px',
                color: '#713200',
                background: '#FEF9C3'
            },
        }
      );
    };

    // 🟢 Listener: Subida de Nivel
    const handleLevelUp = (data) => {
      const { level } = data;
      
      setCurrentUser({ ...currentUser, creatorLevel: level });

      toast.custom((t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-white shadow-2xl rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5 cursor-pointer border-l-4 border-indigo-600`}
          onClick={() => {
            toast.dismiss(t.id);
            navigate(`/profile/${currentUser.username}`);
          }}
        >
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 pt-0.5">
                <div className="h-10 w-10 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 flex items-center justify-center text-white text-xl shadow-md">
                    🚀
                </div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-bold text-gray-900">
                  ¡Nivel {level} Alcanzado!
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Has desbloqueado nuevas funciones de creador. ¡Toca para ver!
                </p>
              </div>
            </div>
          </div>
        </div>
      ), { duration: 6000, position: 'top-center' });
    };

    // 🟢 Listener: Blogger Transmitiendo
    const handleBloggerLive = (data) => {
      const { username, avatar, streamTitle } = data;
      
      toast.custom((t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-gray-900 shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-white ring-opacity-10 cursor-pointer border-l-4 border-red-500`}
          onClick={() => {
            toast.dismiss(t.id);
            navigate(`/live/${username}`);
          }}
        >
          <div className="flex-1 w-0 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 pt-0.5 relative">
                <img className="h-10 w-10 rounded-full object-cover border border-gray-600" src={avatar || '/images/placeholder/user-50.svg'} alt={username} />
                <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-gray-900 bg-red-500 animate-pulse" />
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-bold text-white">{username} está en vivo 🔴</p>
                <p className="mt-1 text-xs text-gray-400 truncate">{streamTitle || '¡Entra y saluda!'}</p>
              </div>
            </div>
          </div>
        </div>
      ), { duration: 5000, position: 'top-right' });
    };

    // 🟢 Listener: Llamada Entrante (El teléfono sonando)
    const handleIncomingCall = (data) => {
      const { caller, type, roomId } = data;
      useChatStore.getState().receiveCall?.(caller, type);
      
      // 🟢 SONIDO REAL: Reproducimos el ringtone
      // Asegúrate de que el archivo exista en public/sounds/ringtone.mp3
      const ringtone = new Audio('/sounds/ringtone.mp3');
      ringtone.loop = true;
      
      // 🟢 FIX: Manejo robusto de la promesa de reproducción para evitar AbortError
      const playPromise = ringtone.play().catch(e => {
        // Ignoramos el error si fue interrumpido por una pausa (AbortError)
        if (e.name !== 'AbortError') {
          console.log("Interacción requerida para reproducir audio:", e);
        }
      });

      const stopRingtone = () => {
        if (playPromise !== undefined) {
          // Esperamos a que termine de intentar reproducir antes de pausar
          playPromise.then(() => ringtone.pause()).catch(() => {});
        }
      };

      toast.custom((t) => (
        <div
          className={`${
            t.visible ? 'animate-enter' : 'animate-leave'
          } max-w-md w-full bg-gray-900 shadow-2xl rounded-lg pointer-events-auto flex flex-col ring-1 ring-white ring-opacity-10 border-l-4 border-green-500`}
        >
          <div className="p-4 flex items-center">
            <div className="flex-shrink-0 pt-0.5 relative">
              <img className="h-12 w-12 rounded-full object-cover border-2 border-green-500" src={caller.avatar || '/images/placeholder/user-50.svg'} alt={caller.username} />
              <span className="absolute bottom-0 right-0 block h-3 w-3 rounded-full ring-2 ring-gray-900 bg-green-500 animate-pulse" />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-bold text-white">Llamada Entrante de {caller.username}</p>
              <p className="mt-1 text-xs text-gray-400">{type === 'video' ? '📹 Videollamada' : '📞 Llamada de Audio'}</p>
            </div>
          </div>
          <div className="flex border-t border-gray-700">
            <button
              onClick={() => {
                toast.dismiss(t.id);
                stopRingtone(); // 🟢 Usamos la función segura
                socket.emit('reject-call', { callerId: caller._id });
                useChatStore.getState().endCall?.();
              }}
              className="w-1/2 border-r border-gray-700 p-3 text-sm font-bold text-red-400 hover:bg-gray-800 transition-colors"
            >
              Rechazar
            </button>
            <button
              onClick={() => {
                toast.dismiss(t.id);
                stopRingtone(); // 🟢 Usamos la función segura
                socket.emit('accept-call', { callerId: caller._id });
                
                // 🟢 Navegación Robusta
                const callPath = `/call/${caller._id}?type=${type}&initiator=false`;
                console.log("Navegando a:", callPath);
                navigate(callPath);
              }}
              className="w-1/2 p-3 text-sm font-bold text-green-400 hover:bg-gray-800 transition-colors"
            >
              Contestar
            </button>
          </div>
        </div>
      ), { duration: 30000, position: 'top-center', id: `call-${roomId}` });
    };

    // 🟢 Listener: Notificación Universal (Toast)
    const handleNewNotification = (notification) => {
        toast(notification.message, {
            icon: '🔔',
            style: {
                borderRadius: '10px',
                background: '#333',
                color: '#fff',
            },
        });
    };

    socket.on('xp-gained', handleXpGained);
    socket.on('level-up', handleLevelUp);
    socket.on('blogger-live-started', handleBloggerLive);
    socket.on('incoming-call', handleIncomingCall); // 🟢 Conectamos el cable del teléfono
    socket.on('call-rejected', () => useChatStore.getState().endCall?.());
    socket.on('new-notification', handleNewNotification); // 🟢 Evento universal

    return () => {
      socket.off('xp-gained', handleXpGained);
      socket.off('level-up', handleLevelUp);
      socket.off('blogger-live-started', handleBloggerLive);
      socket.off('incoming-call', handleIncomingCall);
      socket.off('call-rejected');
      socket.off('new-notification', handleNewNotification);
    };
  }, [socket]); // ✅ SOLO socket - currentUser y setCurrentUser están capturadas en las funciones

  return null;
};

export default NotificationListener;